import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { BUILDINGS_CONFIG } from './BookingContext';
import { getRoomOccupancyStatus, getTodayStr } from '../utils/occupancyStatus';

const RealtimeContext = createContext(null);

export function useRealtime() {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtime must be used within RealtimeProvider');
  return ctx;
}

// ── Build initial rooms from BUILDINGS_CONFIG as fallback ──────────────────
function buildDefaultRooms() {
  let id = 1;
  return Object.entries(BUILDINGS_CONFIG)?.flatMap(([building, cfg]) =>
    cfg?.floors?.flatMap(({ floor, rooms }) =>
      rooms?.map(roomNumber => ({
        id: String(id++),
        room_number: roomNumber,
        building,
        floor,
        room_type: building === 'L' ? 'Apartment' : floor === 0 ? 'Studio' : 'Standard',
        capacity: building === 'L' ? 2 : 1,
        notes: '',
        cleaning_status: 'available',
        is_active: true,
      }))
    )
  );
}

export function RealtimeProvider({ children }) {
  const { user } = useAuth();

  // ── Core data state ────────────────────────────────────────────────────────
  const [stays, setStays] = useState([]);
  const [rooms, setRooms] = useState(buildDefaultRooms());
  const [claims, setClaims] = useState([]);
  const [roomTransfers, setRoomTransfers] = useState([]);
  const [reservationRequests, setReservationRequests] = useState([]);
  const [residents, setResidents] = useState([]);
  const [mealTickets, setMealTickets] = useState([]);
  const [scanLogs, setScanLogs] = useState([]);
  const [cardholders, setCardholders] = useState([]);

  // ── Loading states ─────────────────────────────────────────────────────────
  const [loadingStays, setLoadingStays] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // ── Track subscribed channels ──────────────────────────────────────────────
  const channelsRef = useRef([]);

  // ── Fetch functions ────────────────────────────────────────────────────────

  const fetchStays = useCallback(async () => {
    if (!user?.id) return;
    setLoadingStays(true);
    try {
      // Join with residents to get enriched data
      const { data, error } = await supabase
        ?.from('stays')
        ?.select('*, residents(id, full_name, national_id, passport_id, email, phone, gender, nationality, department, building, room_number, check_in_date, check_out_date, financial_status, notes, resident_category, accommodation_type, passport_photo_url, is_active)')
        ?.order('created_at', { ascending: false });
      if (error) {
        console.error('fetchStays error:', error?.message, error?.code);
      } else if (data) {
        // Merge resident master data into stay records for unified access
        const enriched = data?.map(s => ({
          ...s,
          // Prefer residents master record fields when available
          resident_name: s?.residents?.full_name || s?.resident_name,
          email: s?.residents?.email || s?.email,
          phone: s?.residents?.phone || s?.phone,
          nationality: s?.residents?.nationality || s?.nationality,
          department: s?.residents?.department || s?.department,
          gender: s?.residents?.gender || s?.gender,
          passport_id: s?.residents?.passport_id || s?.passport_id,
          resident_category: s?.residents?.resident_category || s?.resident_category,
          passport_photo_url: s?.residents?.passport_photo_url || s?.passport_photo_url,
        }));
        setStays(enriched);
      }
    } catch (err) {
      console.error('fetchStays exception:', err);
    } finally {
      setLoadingStays(false);
    }
  }, [user?.id]);

  const fetchRooms = useCallback(async () => {
    if (!user?.id) return;
    setLoadingRooms(true);
    try {
      const { data, error } = await supabase?.from('rooms')?.select('*')?.eq('is_active', true)?.order('building')?.order('room_number');
      if (error) {
        console.error('fetchRooms error:', error?.message);
      } else if (data && data?.length > 0) {
        setRooms(data);
      }
    } catch (err) {
      console.error('fetchRooms exception:', err);
    } finally {
      setLoadingRooms(false);
    }
  }, [user?.id]);

  const fetchClaims = useCallback(async () => {
    if (!user?.id) return;
    setLoadingClaims(true);
    try {
      const { data, error } = await supabase
        ?.from('claims')
        ?.select('*, residents(id, full_name, building, room_number)')
        ?.order('created_at', { ascending: false })
        ?.limit(500);
      if (error) {
        console.error('fetchClaims error:', error?.message);
      } else if (data) {
        setClaims(data);
      }
    } catch (err) {
      console.error('fetchClaims exception:', err);
    } finally {
      setLoadingClaims(false);
    }
  }, [user?.id]);

  const fetchRoomTransfers = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        ?.from('room_transfers')
        ?.select('*, residents!room_transfers_resident_id_fkey(id, full_name)')
        ?.order('created_at', { ascending: false });
      if (error) {
        console.error('fetchRoomTransfers error:', error?.message);
      } else if (data) {
        setRoomTransfers(data);
      }
    } catch (err) {
      console.error('fetchRoomTransfers exception:', err);
    }
  }, [user?.id]);

  const fetchReservationRequests = useCallback(async () => {
    if (!user?.id) return;
    setLoadingRequests(true);
    try {
      const { data, error } = await supabase?.from('reservation_requests')?.select('*')?.order('created_at', { ascending: false });
      if (error) {
        console.error('fetchReservationRequests error:', error?.message);
      } else if (data) {
        setReservationRequests(data);
      }
    } catch (err) {
      console.error('fetchReservationRequests exception:', err);
    } finally {
      setLoadingRequests(false);
    }
  }, [user?.id]);

  const fetchResidents = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        ?.from('residents')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('created_at', { ascending: false });
      if (error) {
        console.error('fetchResidents error:', error?.message);
      } else if (data) {
        setResidents(data);
      }
    } catch (err) {
      console.error('fetchResidents exception:', err);
    }
  }, [user?.id]);

  const fetchMealTickets = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        ?.from('restaurant_meal_tickets')
        ?.select('*, restaurant_cardholders(full_name, category, building, department, resident_id)')
        ?.order('created_at', { ascending: false })
        ?.limit(500);
      if (error) {
        console.error('fetchMealTickets error:', error?.message);
      } else if (data) {
        setMealTickets(data);
      }
    } catch (err) {
      console.error('fetchMealTickets exception:', err);
    }
  }, [user?.id]);

  const fetchScanLogs = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('restaurant_scan_logs')?.select('*')?.order('scan_time', { ascending: false })?.limit(200);
      if (error) {
        console.error('fetchScanLogs error:', error?.message);
      } else if (data) {
        setScanLogs(data);
      }
    } catch (err) {
      console.error('fetchScanLogs exception:', err);
    }
  }, [user?.id]);

  const fetchCardholders = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        ?.from('restaurant_cardholders')
        ?.select('*, residents(id, full_name, building, room_number, check_in_date, check_out_date, nationality, department)')
        ?.order('full_name');
      if (error) {
        console.error('fetchCardholders error:', error?.message);
      } else if (data) {
        setCardholders(data);
      }
    } catch (err) {
      console.error('fetchCardholders exception:', err);
    }
  }, [user?.id]);

  // ── Initial data load ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) {
      setStays([]);
      setClaims([]);
      setRoomTransfers([]);
      setReservationRequests([]);
      setResidents([]);
      setMealTickets([]);
      setScanLogs([]);
      setCardholders([]);
      return;
    }

    fetchStays();
    fetchRooms();
    fetchClaims();
    fetchRoomTransfers();
    fetchReservationRequests();
    fetchResidents();
    fetchMealTickets();
    fetchScanLogs();
    fetchCardholders();
  }, [user?.id]);

  // ── Real-time subscriptions ────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;

    channelsRef?.current?.forEach(ch => supabase?.removeChannel(ch));
    channelsRef.current = [];

    const handleChange = (setter) => (payload) => {
      const { eventType, new: newRow, old: oldRow } = payload;
      if (eventType === 'INSERT') {
        setter(prev => {
          if (prev?.find(r => r?.id === newRow?.id)) return prev;
          return [newRow, ...prev];
        });
      } else if (eventType === 'UPDATE') {
        setter(prev => prev?.map(r => r?.id === newRow?.id ? newRow : r));
      } else if (eventType === 'DELETE') {
        setter(prev => prev?.filter(r => r?.id !== oldRow?.id));
      }
    };

    // Stays channel — refetch to get joined resident data
    const staysChannel = supabase?.channel('rt_stays_v3')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'stays' }, (payload) => {
        console.log('Realtime stays event:', payload?.eventType, payload?.new?.id);
        // Refetch to get enriched resident join data
        fetchStays();
      })
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') console.log('✅ Stays realtime subscription active');
      });

    // Residents channel — when resident updates, refresh stays + cardholders
    const residentsChannel = supabase?.channel('rt_residents_v3')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'residents' }, (payload) => {
        console.log('Realtime residents event:', payload?.eventType, payload?.new?.id);
        handleChange(setResidents)(payload);
        // Also refresh stays and cardholders since they join residents
        fetchStays();
        fetchCardholders();
      })
      ?.subscribe();

    // Rooms channel
    const roomsChannel = supabase?.channel('rt_rooms_v3')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'rooms' }, handleChange(setRooms))
      ?.subscribe();

    // Claims channel — refetch to get resident join
    const claimsChannel = supabase?.channel('rt_claims_v3')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'claims' }, () => fetchClaims())
      ?.subscribe();

    // Room transfers channel
    const transfersChannel = supabase?.channel('rt_room_transfers_v3')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'room_transfers' }, () => fetchRoomTransfers())
      ?.subscribe();

    // Reservation requests channel
    const reservationsChannel = supabase?.channel('rt_reservation_requests_v3')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'reservation_requests' }, handleChange(setReservationRequests))
      ?.subscribe();

    // Meal tickets channel — refetch to get joined cardholder data
    const ticketsChannel = supabase?.channel('rt_meal_tickets_v3')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'restaurant_meal_tickets' }, () => fetchMealTickets())
      ?.subscribe();

    // Scan logs channel
    const scanLogsChannel = supabase?.channel('rt_scan_logs_v3')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'restaurant_scan_logs' }, handleChange(setScanLogs))
      ?.subscribe();

    // Cardholders channel — refetch to get resident join
    const cardholdersChannel = supabase?.channel('rt_cardholders_v3')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'restaurant_cardholders' }, () => fetchCardholders())
      ?.subscribe();

    channelsRef.current = [
      staysChannel, residentsChannel, roomsChannel, claimsChannel, transfersChannel,
      reservationsChannel, ticketsChannel, scanLogsChannel, cardholdersChannel
    ];

    return () => {
      channelsRef?.current?.forEach(ch => supabase?.removeChannel(ch));
      channelsRef.current = [];
    };
  }, [user?.id, fetchStays, fetchClaims, fetchRoomTransfers, fetchMealTickets, fetchCardholders]);

  // ── Derived computed values ────────────────────────────────────────────────

  const getRoomStatus = useCallback((roomNumber) => {
    const today = getTodayStr();
    const roomRecord = rooms?.find(r => (r?.room_number || r?.roomNumber) === roomNumber);
    const status = getRoomOccupancyStatus(roomNumber, stays, roomRecord, today);
    // Map to cleaning_status values used by Room Management
    if (status === 'Occupied')     return 'occupied';
    if (status === 'Reserved')     return 'reserved';
    if (status === 'Maintenance')  return 'maintenance';
    return 'available';
  }, [stays, rooms]);

  const occupancyMetrics = React.useMemo(() => {
    // Use DB rooms count if available, otherwise fall back to BUILDINGS_CONFIG total
    const dbRoomsCount = rooms?.length || 0;
    const configTotalRooms = Object.values(BUILDINGS_CONFIG)?.reduce(
      (sum, cfg) => sum + cfg?.floors?.reduce((s, f) => s + f?.rooms?.length, 0), 0
    );
    const totalRooms = dbRoomsCount > 0 ? dbRoomsCount : configTotalRooms;

    const today = getTodayStr();

    // Date-driven counts: iterate all rooms from BUILDINGS_CONFIG
    let occupiedRooms = 0;
    let reservedRooms = 0;

    const allRoomNumbers = Object.values(BUILDINGS_CONFIG)?.flatMap(cfg =>
      cfg?.floors?.flatMap(f => f?.rooms)
    );

    allRoomNumbers?.forEach(roomNumber => {
      const roomRecord = rooms?.find(r => (r?.room_number || r?.roomNumber) === roomNumber);
      const status = getRoomOccupancyStatus(roomNumber, stays, roomRecord, today);
      if (status === 'Occupied')    occupiedRooms++;
      else if (status === 'Reserved') reservedRooms++;
    });

    const availableRooms = Math.max(0, totalRooms - occupiedRooms - reservedRooms);
    const occupancyRate = totalRooms > 0 ? ((occupiedRooms / totalRooms) * 100)?.toFixed(1) : '0.0';
    const pendingCheckIns = stays?.filter(s => {
      const ci = s?.check_in_date || s?.checkIn || '';
      return (s?.status === 'active-reservation' || s?.status === 'pending') && ci === today;
    })?.length;
    const pendingCheckOuts = stays?.filter(s => {
      const co = s?.check_out_date || s?.checkOut || '';
      return (s?.status === 'confirmed-stay' || s?.status === 'active') && co === today;
    })?.length;
    // Active residents = those currently occupying a room (date-driven)
    const activeResidents = occupiedRooms;
    // Upcoming departures = occupied stays with checkout within next 7 days
    const sevenDaysLater = new Date();
    sevenDaysLater?.setDate(sevenDaysLater?.getDate() + 7);
    const sevenDaysStr = sevenDaysLater?.toISOString()?.split('T')?.[0];
    const upcomingDepartures = stays?.filter(s => {
      const co = s?.check_out_date || s?.checkOut || '';
      const ci = s?.check_in_date  || s?.checkIn  || '';
      return (s?.status === 'confirmed-stay' || s?.status === 'active') &&
             today >= ci && today < co &&
             co >= today && co <= sevenDaysStr;
    })?.length;
    return {
      totalRooms,
      occupiedRooms,
      reservedRooms,
      availableRooms,
      occupancyRate,
      pendingCheckIns,
      pendingCheckOuts,
      activeResidents,
      upcomingDepartures,
    };
  }, [stays, rooms]);

  // ── Per-building occupancy stats for threshold alerts ──────────────────────
  const buildingOccupancyStats = React.useMemo(() => {
    const today = getTodayStr();
    return Object.entries(BUILDINGS_CONFIG)?.map(([building, cfg]) => {
      const total = cfg?.floors?.reduce((sum, f) => sum + f?.rooms?.length, 0);
      let occupied = 0;
      cfg?.floors?.forEach(f => {
        f?.rooms?.forEach(roomNumber => {
          const roomRecord = rooms?.find(r => (r?.room_number || r?.roomNumber) === roomNumber);
          const status = getRoomOccupancyStatus(roomNumber, stays, roomRecord, today);
          if (status === 'Occupied') occupied++;
        });
      });
      const rate = total > 0 ? parseFloat(((occupied / total) * 100)?.toFixed(1)) : 0;
      return { building, total, occupied, rate };
    });
  }, [stays, rooms]);

  const mealMetrics = React.useMemo(() => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const todayTickets = mealTickets?.filter(t => t?.valid_date === today);
    const totalServedToday = todayTickets?.filter(t => t?.status === 'used')?.length;
    const breakfastToday = todayTickets?.filter(t => t?.meal_type === 'breakfast' && t?.status === 'used')?.length;
    const lunchToday = todayTickets?.filter(t => t?.meal_type === 'lunch' && t?.status === 'used')?.length;
    const dinnerToday = todayTickets?.filter(t => t?.meal_type === 'dinner' && t?.status === 'used')?.length;
    const activeTickets = mealTickets?.filter(t => t?.status === 'active')?.length;
    const usedTickets = mealTickets?.filter(t => t?.status === 'used')?.length;
    return { totalServedToday, breakfastToday, lunchToday, dinnerToday, activeTickets, usedTickets };
  }, [mealTickets]);

  // ── CRUD operations ────────────────────────────────────────────────────────

  // Helper: sync room status in rooms table based on stay status
  const syncRoomStatus = useCallback(async (roomNumber, building, stayStatus) => {
    if (!roomNumber || !building) return;
    let cleaningStatus = 'available';
    if (stayStatus === 'confirmed-stay' || stayStatus === 'active') {
      cleaningStatus = 'occupied';
    } else if (stayStatus === 'active-reservation' || stayStatus === 'pending') {
      cleaningStatus = 'reserved';
    } else if (stayStatus === 'completed-stay' || stayStatus === 'checked-out') {
      cleaningStatus = 'needs-cleaning';
    }
    try {
      await supabase
        ?.from('rooms')
        ?.update({ cleaning_status: cleaningStatus, updated_at: new Date()?.toISOString() })
        ?.eq('room_number', roomNumber)
        ?.eq('building', building);
    } catch (err) {
      console.warn('syncRoomStatus error (non-critical):', err?.message);
    }
  }, []);

  // Stays CRUD — DB triggers handle resident sync automatically
  const addStay = useCallback(async (stayData) => {
    if (!user?.id) {
      console.error('addStay: No authenticated user');
      return { data: null, error: new Error('Not authenticated') };
    }
    try {
      const insertPayload = {
        resident_name: stayData?.residentName || stayData?.resident_name || '',
        resident_id_code: stayData?.residentId || stayData?.resident_id_code || null,
        guest_id: null,
        email: stayData?.email || null,
        phone: stayData?.phone || null,
        nationality: stayData?.nationality || null,
        gender: stayData?.gender || null,
        passport_id: stayData?.passportId || stayData?.passport_id || null,
        resident_category: stayData?.residentCategory || stayData?.resident_category || null,
        passport_photo_url: stayData?.passportPhotoUrl || stayData?.passport_photo_url || null,
        department: stayData?.department || null,
        room_number: stayData?.room || stayData?.room_number || '',
        building: stayData?.building || '',
        floor: stayData?.floor || 0,
        check_in_date: stayData?.checkIn || stayData?.check_in_date || null,
        check_out_date: stayData?.checkOut || stayData?.check_out_date || null,
        status: stayData?.status || 'confirmed-stay',
        guest_status: stayData?.guestStatus || stayData?.guest_status || 'checked-in',
        accommodation_type: stayData?.accommodationType || stayData?.accommodation_type || 'short-term',
        financial_status: stayData?.financialStatus || stayData?.financial_status || 'not_covered_by_senghor',
        notes: stayData?.notes || null,
        from_reservation_request: stayData?.fromReservationRequest || false,
        reservation_request_id: stayData?.reservationRequestId || null,
        created_by: user?.id,
        // resident_id will be set automatically by DB trigger trg_stays_after_insert
      };

      console.log('addStay payload:', insertPayload);

      const { data, error } = await supabase?.from('stays')?.insert(insertPayload)?.select()?.single();
      if (error) {
        console.error('addStay Supabase error:', error?.message, error?.code, error?.details);
        return { data: null, error };
      }
      console.log('✅ addStay success:', data?.id, '— DB trigger will sync residents, cardholders automatically');

      // Sync room status in rooms table immediately
      await syncRoomStatus(
        insertPayload?.room_number,
        insertPayload?.building,
        insertPayload?.status
      );

      // Refresh residents and cardholders after a short delay to pick up trigger results
      setTimeout(() => {
        fetchResidents();
        fetchCardholders();
        fetchStays();
        fetchRooms();
      }, 800);
      return { data, error: null };
    } catch (err) {
      console.error('addStay exception:', err);
      return { data: null, error: err };
    }
  }, [user?.id, fetchResidents, fetchCardholders, fetchStays, fetchRooms, syncRoomStatus]);

  const updateStay = useCallback(async (stayId, updates) => {
    try {
      if (!stayId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(String(stayId))) {
        console.warn('updateStay: invalid stayId:', stayId);
        return { data: null, error: new Error('Invalid stayId: not a UUID') };
      }
      const dbUpdates = { updated_at: new Date()?.toISOString() };
      if (updates?.status !== undefined) dbUpdates.status = updates?.status;
      if (updates?.guestStatus !== undefined) dbUpdates.guest_status = updates?.guestStatus;
      if (updates?.guest_status !== undefined) dbUpdates.guest_status = updates?.guest_status;
      if (updates?.checkOut !== undefined) dbUpdates.check_out_date = updates?.checkOut;
      if (updates?.check_out_date !== undefined) dbUpdates.check_out_date = updates?.check_out_date;
      if (updates?.actual_check_out !== undefined) dbUpdates.actual_check_out = updates?.actual_check_out;
      if (updates?.room !== undefined) dbUpdates.room_number = updates?.room;
      if (updates?.room_number !== undefined) dbUpdates.room_number = updates?.room_number;
      if (updates?.building !== undefined) dbUpdates.building = updates?.building;
      if (updates?.keyReturned !== undefined) dbUpdates.key_returned = updates?.keyReturned;
      if (updates?.roomCheckCompleted !== undefined) dbUpdates.room_check_completed = updates?.roomCheckCompleted;
      if (updates?.notes !== undefined) dbUpdates.notes = updates?.notes;

      const { data, error } = await supabase?.from('stays')?.update(dbUpdates)?.eq('id', stayId)?.select()?.single();
      if (error) {
        console.error('updateStay error:', error?.message);
        return { data: null, error };
      }

      // Sync room status in rooms table when stay status changes
      if (updates?.status !== undefined && data?.room_number && data?.building) {
        await syncRoomStatus(data?.room_number, data?.building, updates?.status);
      }

      // DB trigger trg_stays_after_update handles resident/cardholder sync automatically
      setTimeout(() => {
        fetchResidents();
        fetchCardholders();
        fetchRooms();
      }, 800);
      return { data, error: null };
    } catch (err) {
      console.error('updateStay exception:', err);
      return { data: null, error: err };
    }
  }, [fetchResidents, fetchCardholders, fetchRooms, syncRoomStatus]);

  const performCheckIn = useCallback(async (stayId) => {
    return updateStay(stayId, { status: 'confirmed-stay', guestStatus: 'checked-in' });
  }, [updateStay]);

  const processCheckOut = useCallback(async (stayId, actualCheckOut) => {
    return updateStay(stayId, {
      status: 'completed-stay',
      guestStatus: 'checked-out',
      actual_check_out: actualCheckOut,
    });
  }, [updateStay]);

  const deleteStay = useCallback(async (stayId) => {
    try {
      if (!stayId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(String(stayId))) {
        return { error: new Error('Invalid stayId') };
      }
      const { error } = await supabase?.from('stays')?.delete()?.eq('id', stayId);
      if (error) {
        console.error('deleteStay error:', error?.message);
        return { error };
      }
      return { error: null };
    } catch (err) {
      console.error('deleteStay exception:', err);
      return { error: err };
    }
  }, []);

  const addApprovedReservation = useCallback(async (requestData) => {
    const building = requestData?.preferred_building
      ? requestData?.preferred_building?.split('-')?.[0] || requestData?.preferred_building
      : 'F1';

    const allRoomsInBuilding = BUILDINGS_CONFIG?.[building]?.floors?.flatMap(f => f?.rooms) || [];
    const occupiedRoomNumbers = new Set(
      stays
        .filter(s => {
          if (s.status === 'completed-stay') return false;
          const sIn = s.check_in_date || s.checkIn;
          const sOut = s.check_out_date || s.checkOut;
          return sIn < requestData.requested_check_out && sOut > requestData.requested_check_in;
        })
        .map(s => s.room_number || s.room)
    );
    const availableRoom = allRoomsInBuilding?.find(r => !occupiedRoomNumbers?.has(r));
    const assignedRoom = availableRoom || (allRoomsInBuilding?.[0] || `${building}-101`);

    return addStay({
      residentName: requestData?.full_name || 'Guest',
      residentId: `REQ-${requestData?.id?.slice(0, 8) || Date.now()}`,
      email: requestData?.email || '',
      phone: requestData?.phone || '',
      nationality: requestData?.nationality || '',
      department: requestData?.department || '',
      room_number: assignedRoom,
      building,
      checkIn: requestData?.requested_check_in,
      checkOut: requestData?.requested_check_out,
      status: 'active-reservation',
      guestStatus: 'awaiting-arrival',
      accommodationType: 'short-term',
      financialStatus: requestData?.financial_status || 'not_covered_by_senghor',
      fromReservationRequest: true,
      reservationRequestId: requestData?.id,
    });
  }, [stays, addStay]);

  // Claims CRUD
  const addClaim = useCallback(async (claimData) => {
    try {
      const { data, error } = await supabase?.from('claims')?.insert({
        guest_name: claimData?.guestName || claimData?.guest_name || '',
        room: claimData?.room || '',
        building: claimData?.building || null,
        description: claimData?.description || '',
        reported_by: claimData?.reportedBy || claimData?.reported_by || null,
        reported_by_id: user?.id || null,
        stay_id: claimData?.stayId || claimData?.stay_id || null,
        status: 'open',
        photos: claimData?.photos ? JSON.stringify(claimData?.photos) : '[]',
        report_date: new Date()?.toISOString()?.split('T')?.[0],
        notes: claimData?.notes || null,
        // resident_id set automatically by DB trigger trg_claims_after_insert
      })?.select()?.single();
      if (error) {
        console.error('addClaim error:', error?.message);
        return { data: null, error };
      }
      return { data, error: null };
    } catch (err) {
      console.error('addClaim exception:', err);
      return { data: null, error: err };
    }
  }, [user?.id]);

  const updateClaim = useCallback(async (claimId, updates) => {
    try {
      const { data, error } = await supabase?.from('claims')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.eq('id', claimId)?.select()?.single();
      if (error) {
        console.error('updateClaim error:', error?.message);
        return { data: null, error };
      }
      return { data, error: null };
    } catch (err) {
      console.error('updateClaim exception:', err);
      return { data: null, error: err };
    }
  }, []);

  const deleteClaim = useCallback(async (claimId) => {
    try {
      const { error } = await supabase?.from('claims')?.delete()?.eq('id', claimId);
      if (error) {
        console.error('deleteClaim error:', error?.message);
        return { error };
      }
      return { error: null };
    } catch (err) {
      console.error('deleteClaim exception:', err);
      return { error: err };
    }
  }, []);

  // Room transfer — DB trigger sets resident_id automatically
  const addRoomTransfer = useCallback(async (transferData) => {
    try {
      if (transferData?.stayId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(String(transferData?.stayId))) {
        const { error: stayUpdateError } = await supabase?.from('stays')?.update({
          room_number: transferData?.toRoom,
          building: transferData?.toBuilding,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', transferData?.stayId);
        if (stayUpdateError) {
          console.error('addRoomTransfer stay update error:', stayUpdateError?.message);
        }
        // DB trigger trg_stays_after_update handles resident room sync automatically
      }
      const { data, error } = await supabase?.from('room_transfers')?.insert({
        stay_id: transferData?.stayId || null,
        resident_name: transferData?.residentName || '',
        from_room: transferData?.fromRoom || '',
        from_building: transferData?.fromBuilding || '',
        to_room: transferData?.toRoom || '',
        to_building: transferData?.toBuilding || '',
        transfer_date: transferData?.transferDate || new Date()?.toISOString()?.split('T')?.[0],
        reason: transferData?.reason || null,
        notes: transferData?.notes || null,
        performed_by: user?.id || null,
        // resident_id set automatically by DB trigger trg_room_transfers_after_insert
      })?.select()?.single();
      if (error) {
        console.error('addRoomTransfer error:', error?.message);
        return { data: null, error };
      }
      return { data, error: null };
    } catch (err) {
      console.error('addRoomTransfer exception:', err);
      return { data: null, error: err };
    }
  }, [user?.id]);

  // Rooms CRUD — Supabase-backed
  const addRoom = useCallback(async (roomData) => {
    try {
      const { data, error } = await supabase?.from('rooms')?.insert({
        room_number: roomData?.roomNumber || roomData?.room_number || '',
        building: roomData?.building || '',
        floor: roomData?.floor || 0,
        room_type: roomData?.type || roomData?.room_type || 'Standard',
        capacity: roomData?.capacity || 1,
        notes: roomData?.notes || null,
        cleaning_status: 'available',
        is_active: true,
      })?.select()?.single();
      if (error) {
        console.error('addRoom error:', error?.message);
        return { data: null, error };
      }
      return { data, error: null };
    } catch (err) {
      console.error('addRoom exception:', err);
      return { data: null, error: err };
    }
  }, []);

  const updateRoom = useCallback(async (roomId, updates) => {
    try {
      const dbUpdates = { updated_at: new Date()?.toISOString() };
      if (updates?.roomNumber !== undefined) dbUpdates.room_number = updates?.roomNumber;
      if (updates?.building !== undefined) dbUpdates.building = updates?.building;
      if (updates?.floor !== undefined) dbUpdates.floor = updates?.floor;
      if (updates?.type !== undefined) dbUpdates.room_type = updates?.type;
      if (updates?.capacity !== undefined) dbUpdates.capacity = updates?.capacity;
      if (updates?.notes !== undefined) dbUpdates.notes = updates?.notes;
      if (updates?.cleaning_status !== undefined) dbUpdates.cleaning_status = updates?.cleaning_status;

      const { data, error } = await supabase?.from('rooms')?.update(dbUpdates)?.eq('id', roomId)?.select()?.single();
      if (error) {
        console.error('updateRoom error:', error?.message);
        return { data: null, error };
      }
      return { data, error: null };
    } catch (err) {
      console.error('updateRoom exception:', err);
      return { data: null, error: err };
    }
  }, []);

  const deleteRoom = useCallback(async (roomId) => {
    try {
      const { error } = await supabase?.from('rooms')?.update({ is_active: false })?.eq('id', roomId);
      if (error) {
        console.error('deleteRoom error:', error?.message);
        return { error };
      }
      return { error: null };
    } catch (err) {
      console.error('deleteRoom exception:', err);
      return { error: err };
    }
  }, []);

  const updateRoomCleaningStatus = useCallback(async (roomNumber, building, cleaningStatus) => {
    try {
      const { error } = await supabase?.from('rooms')?.update({ cleaning_status: cleaningStatus, updated_at: new Date()?.toISOString() })?.eq('room_number', roomNumber)?.eq('building', building);
      if (error) {
        console.error('updateRoomCleaningStatus error:', error?.message);
        return { error };
      }
      return { error: null };
    } catch (err) {
      console.error('updateRoomCleaningStatus exception:', err);
      return { error: err };
    }
  }, []);

  // Residents CRUD — master record operations
  const addResident = useCallback(async (residentData) => {
    try {
      const { data, error } = await supabase?.from('residents')?.insert({
        full_name: residentData?.full_name || residentData?.fullName || '',
        national_id: residentData?.national_id || residentData?.nationalId || null,
        passport_id: residentData?.passport_id || residentData?.passportId || null,
        email: residentData?.email || null,
        phone: residentData?.phone || null,
        gender: residentData?.gender || null,
        nationality: residentData?.nationality || null,
        department: residentData?.department || null,
        building: residentData?.building || null,
        room_number: residentData?.room_number || residentData?.roomNumber || null,
        check_in_date: residentData?.check_in_date || residentData?.checkIn || null,
        check_out_date: residentData?.check_out_date || residentData?.checkOut || null,
        financial_status: residentData?.financial_status || 'not_covered_by_senghor',
        notes: residentData?.notes || null,
        resident_category: residentData?.resident_category || residentData?.residentCategory || null,
        accommodation_type: residentData?.accommodation_type || 'long-term',
        passport_photo_url: residentData?.passport_photo_url || null,
        created_by: user?.id || null,
        is_active: true,
      })?.select()?.single();
      if (error) {
        console.error('addResident error:', error?.message);
        return { data: null, error };
      }
      return { data, error: null };
    } catch (err) {
      console.error('addResident exception:', err);
      return { data: null, error: err };
    }
  }, [user?.id]);

  const updateResident = useCallback(async (residentId, updates) => {
    try {
      const { data, error } = await supabase
        ?.from('residents')
        ?.update({ ...updates, updated_at: new Date()?.toISOString() })
        ?.eq('id', residentId)
        ?.select()
        ?.single();
      if (error) {
        console.error('updateResident error:', error?.message);
        return { data: null, error };
      }
      // DB trigger trg_residents_after_update propagates changes to stays + cardholders
      setTimeout(() => {
        fetchStays();
        fetchCardholders();
      }, 600);
      return { data, error: null };
    } catch (err) {
      console.error('updateResident exception:', err);
      return { data: null, error: err };
    }
  }, [fetchStays, fetchCardholders]);

  const deleteResident = useCallback(async (residentId) => {
    try {
      const { error } = await supabase
        ?.from('residents')
        ?.update({ is_active: false, updated_at: new Date()?.toISOString() })
        ?.eq('id', residentId);
      if (error) {
        console.error('deleteResident error:', error?.message);
        return { error };
      }
      return { error: null };
    } catch (err) {
      console.error('deleteResident exception:', err);
      return { error: err };
    }
  }, []);

  // ── Centralized sync helper ────────────────────────────────────────────────
  // Call this to manually trigger a full cross-module refresh
  const syncResidentAcrossModules = useCallback(async (residentId) => {
    if (!residentId) {
      // Full refresh of all modules
      await Promise.all([
        fetchStays(),
        fetchResidents(),
        fetchCardholders(),
        fetchClaims(),
        fetchRoomTransfers(),
        fetchMealTickets(),
        fetchReservationRequests(),
      ]);
      return;
    }
    // Targeted refresh for a specific resident
    try {
      const { data } = await supabase?.rpc('get_resident_full_profile', { p_resident_id: residentId });
      if (data) {
        console.log('✅ Resident full profile synced:', residentId, data);
      }
    } catch (err) {
      console.error('syncResidentAcrossModules error:', err);
    }
    // Always refresh all related state
    await Promise.all([
      fetchStays(),
      fetchResidents(),
      fetchCardholders(),
      fetchClaims(),
      fetchRoomTransfers(),
    ]);
  }, [fetchStays, fetchResidents, fetchCardholders, fetchClaims, fetchRoomTransfers, fetchMealTickets, fetchReservationRequests]);

  // Refresh all data manually
  const refreshAll = useCallback(() => {
    fetchStays();
    fetchRooms();
    fetchClaims();
    fetchRoomTransfers();
    fetchReservationRequests();
    fetchResidents();
    fetchMealTickets();
    fetchScanLogs();
    fetchCardholders();
  }, [fetchStays, fetchRooms, fetchClaims, fetchRoomTransfers, fetchReservationRequests, fetchResidents, fetchMealTickets, fetchScanLogs, fetchCardholders]);

  const value = {
    // Data
    stays,
    rooms,
    claims,
    roomTransfers,
    reservationRequests,
    residents,
    mealTickets,
    scanLogs,
    cardholders,

    // Loading
    loadingStays,
    loadingRooms,
    loadingClaims,
    loadingRequests,

    // Derived
    getRoomStatus,
    occupancyMetrics,
    buildingOccupancyStats,
    mealMetrics,

    // Stays operations
    addStay,
    updateStay,
    deleteStay,
    performCheckIn,
    processCheckOut,
    addApprovedReservation,

    // Claims operations
    addClaim,
    updateClaim,
    deleteClaim,

    // Room transfer
    addRoomTransfer,

    // Rooms CRUD (Supabase-backed)
    addRoom,
    updateRoom,
    deleteRoom,
    updateRoomCleaningStatus,

    // Residents CRUD (master entity)
    addResident,
    updateResident,
    deleteResident,

    // Centralized sync
    syncResidentAcrossModules,

    // Refresh
    refreshAll,
    fetchStays,
    fetchRooms,
    fetchClaims,
    fetchMealTickets,
    fetchScanLogs,
    fetchCardholders,
    fetchReservationRequests,
    fetchResidents,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}
